import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18next';
import BrandConfigManager from '../services/api/BrandConfigManager';
import { BRAND_MODES, FOOD_AMIGOS_BRAND, NEUTRAL_BRAND } from '../services/branding/brands';
import { brandFromConfigResponse, brandFromServiceProvider } from '../services/branding/brandFromServiceProvider';
import { readCachedBrand, writeCachedBrand } from '../services/branding/brandCache';
import { applyBrandColor, clearBrandColor } from '../services/branding/brandTheme';
import { isFoodAmigosHost } from '../services/branding/hosts';
import { resolvePartnerSlug } from '../services/branding/partnerSlugStorage';
import { resolveBrandLanguage } from '../services/localization/brandLanguages';

export { BRAND_MODES };

export const BrandContext = createContext(null);

export function useBrand() {
  return useContext(BrandContext);
}

// Logged-out brand when neither the host nor a slug names a partner: FoodAmigos on its own
// hosts, elsewhere the brand last resolved on this browser (by host, slug or staff session),
// else the neutral one.
function fallbackBrand(hostname) {
  if (isFoodAmigosHost(hostname)) {
    return FOOD_AMIGOS_BRAND;
  }

  return readCachedBrand(hostname) ?? NEUTRAL_BRAND;
}

// Off FoodAmigos' own hosts the hostname is sent to brand-config: it may be a partner's own
// orders host (`orders.<their root>`), which names the brand without any slug.
function brandConfigHost(hostname) {
  return isFoodAmigosHost(hostname) ? null : hostname;
}

// Synchronous starting point so the first paint never shows FoodAmigos on a partner
// screen: a persisted session brands itself immediately, a slug or a non-FoodAmigos host
// starts from the cached brand and settles once /brand-config answers, anything else
// settles on the fallback.
function initialBrandState({ serviceProvider, locales, slug, hostname }) {
  const sessionBrand = brandFromServiceProvider(serviceProvider, locales);

  if (sessionBrand) {
    return { brand: sessionBrand, settled: true };
  }

  if (slug || brandConfigHost(hostname)) {
    return { brand: readCachedBrand(hostname) ?? NEUTRAL_BRAND, settled: false };
  }

  return { brand: fallbackBrand(hostname), settled: true };
}

// Mounted outside the Router (App.jsx), so the slug is read from window.location
// rather than router hooks. `?partner=` wins and is persisted for the tab, otherwise the
// slug persisted earlier in this tab is reused. The initializer resolves it synchronously so
// a magic-link `?partner=` is already persisted when LoginView's validateToken() runs.
// The hostname picks a partner brand too: a partner can serve the portal on their own
// domain, where a bare visit (a typed address, a bookmark) carries no slug. A host no partner
// serves answers neutral, and a neutral answer to the host alone keeps the cached brand.
// Only a host, a slug or a session rewrites the cached brand; logging out clears nothing.
export default function BrandProvider({ children }) {
  const hostname = window.location.hostname;
  const serviceProvider = useSelector((state) => state.company.company?.service_provider);
  // Fallback only: the partner's own `languages` (on service_provider) are the session's
  // switchable languages, and these region locales stand in for a brand cached before
  // that field shipped. See brandFromServiceProvider.
  const locales = useSelector((state) => state.company.company?.locales);
  // The same test brandFromServiceProvider applies: a persisted `{}` hole (FOODAMIGOS-OMS-WEB-6G)
  // is not a session. Counting it as one would leave a logged-out screen waiting on a
  // brand-config answer this effect never asks for.
  const hasSession = Boolean(serviceProvider?.id);

  const [slug, setSlug] = useState(() => resolvePartnerSlug(window.location.search));
  const [{ brand, settled }, setState] = useState(() =>
    initialBrandState({ serviceProvider, locales, slug, hostname }),
  );

  // Logged out: brand by slug (fetched), else the cached / host brand. Re-runs only when the
  // session goes away, so the login → /staff/profile gap keeps the brand the login page had.
  // The slug is re-resolved here rather than taken from the initializer: a tab opened while
  // logged in has none until logOut() syncs the session's slug into sessionStorage right
  // before the store is cleared.
  useEffect(() => {
    if (hasSession) {
      return;
    }

    const currentSlug = resolvePartnerSlug(window.location.search);
    const host = brandConfigHost(hostname);
    setSlug(currentSlug);

    if (!currentSlug && !host) {
      setState({ brand: fallbackBrand(hostname), settled: true });

      return;
    }

    let cancelled = false;

    BrandConfigManager.get({ partner: currentSlug, host }).then(({ success, data }) => {
      if (cancelled) {
        return;
      }

      if (!success) {
        // Unreachable brand-config: keep whatever brands the page already.
        setState((current) => ({ brand: current.brand, settled: true }));

        return;
      }

      const fetched = brandFromConfigResponse(data);

      // Asked by host alone and answered neutral: not a host any partner serves on (yet).
      // Keep what this browser last resolved here rather than blank the login page.
      if (!currentSlug && fetched.mode === BRAND_MODES.NEUTRAL) {
        setState({ brand: fallbackBrand(hostname), settled: true });

        return;
      }

      writeCachedBrand(fetched, hostname);
      setState({ brand: fetched, settled: true });
    });

    return () => {
      cancelled = true;
    };
  }, [hasSession, hostname]);

  // Logged in: the session's service provider is the brand (master → FoodAmigos) and is
  // cached for the next logged-out screen. A missing provider keeps the previous brand — no
  // flash between login and profile.
  useEffect(() => {
    const sessionBrand = brandFromServiceProvider(serviceProvider, locales);

    if (sessionBrand) {
      writeCachedBrand(sessionBrand, hostname);
      setState({ brand: sessionBrand, settled: true });
    }
  }, [serviceProvider, locales, hostname]);

  // The UI language follows the brand: a language the brand does not offer (a German
  // session opening a Spanish partner's login) switches to the brand default — the
  // partner's language once translated, English until then.
  useEffect(() => {
    // `isInitialized` guards renders before Localization.js ran (unit tests).
    if (!settled || !i18n.isInitialized) {
      return;
    }

    const language = resolveBrandLanguage(brand, i18n.language);

    if (language !== i18n.language) {
      i18n.changeLanguage(language);
    }
  }, [brand, settled]);

  useEffect(() => {
    if (brand.mode === BRAND_MODES.FOOD_AMIGOS) {
      clearBrandColor();

      return;
    }

    applyBrandColor(brand.primaryColor);
  }, [brand]);

  const value = useMemo(
    () => ({
      ...brand,
      isWhiteLabel: brand.mode !== BRAND_MODES.FOOD_AMIGOS,
      isNeutral: brand.mode === BRAND_MODES.NEUTRAL,
      settled,
      partnerSlug: slug,
    }),
    [brand, settled, slug],
  );

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}
